/**
 * Simple HTML sanitizer  allows safe formatting tags, strips everything dangerous.
 * Prevents XSS while allowing basic game text formatting (bold, italic, colors, etc.).
 */

const ALLOWED_TAGS = new Set([
  'b', 'i', 'em', 'strong', 'br', 'span', 'div', 'p', 'u', 'small',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'hr'
]);

const ALLOWED_ATTRS = new Set(['class', 'style']);

// Only allow safe CSS properties in style attributes
const SAFE_STYLE_PROPS = new Set([
  'color', 'background-color', 'font-weight', 'font-style', 'font-size',
  'text-decoration', 'text-align', 'margin', 'padding', 'display',
  'opacity', 'border', 'border-radius'
]);

function sanitizeStyle(styleStr) {
  if (!styleStr) return '';
  return styleStr
    .split(';')
    .map(rule => rule.trim())
    .filter(rule => {
      const colonIdx = rule.indexOf(':');
      if (colonIdx === -1) return false;
      const prop = rule.substring(0, colonIdx).trim().toLowerCase();
      const value = rule.substring(colonIdx + 1).trim().toLowerCase();
      // Block url(), expression(), javascript:, data: in values
      if (/url\s*\(|expression\s*\(|javascript:|data:/i.test(value)) return false;
      return SAFE_STYLE_PROPS.has(prop);
    })
    .join('; ');
}

export default function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';

  return html
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove style tags and their content
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    // Remove all event handler attributes (onload, onerror, onclick, etc.)
    .replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    // Remove javascript: protocol in href/src/action attributes
    .replace(/\s+(?:href|src|action|formaction)\s*=\s*(?:"[^"]*javascript:[^"]*"|'[^']*javascript:[^']*')/gi, '')
    // Remove data: protocol in src/href (can embed scripts)
    .replace(/\s+(?:href|src)\s*=\s*(?:"[^"]*data:[^"]*"|'[^']*data:[^']*')/gi, '')
    // Process all HTML tags
    .replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/gi, (match, tagName) => {
      const tag = tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return '';

      // Closing tags
      if (match.startsWith('</')) {
        return `</${tag}>`;
      }

      // Parse and filter attributes for opening tags
      const attrRegex = /\s+([a-zA-Z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+))/g;
      let attrs = '';
      let attrMatch;
      while ((attrMatch = attrRegex.exec(match)) !== null) {
        const attrName = attrMatch[1].toLowerCase();
        const attrValue = attrMatch[2] !== undefined ? attrMatch[2] : (attrMatch[3] !== undefined ? attrMatch[3] : (attrMatch[4] || ''));
        if (ALLOWED_ATTRS.has(attrName)) {
          if (attrName === 'style') {
            const safeStyle = sanitizeStyle(attrValue);
            if (safeStyle) attrs += ` style="${safeStyle}"`;
          } else {
            // Block javascript:/data: in any attribute value
            if (!/javascript:|data:/i.test(attrValue)) {
              attrs += ` ${attrName}="${attrValue.replace(/"/g, '&quot;')}"`;
            }
          }
        }
      }

      const selfClosing = tag === 'br' || tag === 'hr';
      return selfClosing ? `<${tag}${attrs} />` : `<${tag}${attrs}>`;
    });
}
